import { useMemo } from 'react';

export function useTaskFilters(tasks, taskGroups, filterTags, filterProjectIds = [], filterCategoryIds = [], filterPriorities = [], filterAttributes = []) {
    // Filter tasks logic
    const filteredTasks = useMemo(() => {
        let result = tasks;

        // Filter by Tags
        if (filterTags && filterTags.length > 0) {
            result = result.filter(task => {
                const taskTagIds = (task.tags || []).map(t => t.id);
                return filterTags.some(tagId => taskTagIds.includes(tagId));
            });
        }

        // Filter by Projects
        if (filterProjectIds && filterProjectIds.length > 0) {
            result = result.filter(task => filterProjectIds.includes(task.project_id));
        }

        // Filter by Categories (Task Groups / Sections)
        if (filterCategoryIds && filterCategoryIds.length > 0) {
            result = result.filter(task => {
                const sectionId = task.section_id || 'uncategorized';
                // If 'uncategorized' is selected, show tasks with null section_id or explicitly 'uncategorized'
                if (filterCategoryIds.includes('uncategorized')) {
                    if (!task.section_id || task.section_id === 'uncategorized') return true;
                }
                return filterCategoryIds.includes(task.section_id);
            });
        }

        // Filter by Priority
        if (filterPriorities && filterPriorities.length > 0) {
            result = result.filter(task => filterPriorities.includes(task.priority || 1)); // Default to 1 (Low) if undefined
        }

        // Filter by Attributes
        if (filterAttributes && filterAttributes.length > 0) {
            result = result.filter(task => {
                return filterAttributes.every(attr => {
                    switch (attr) {
                        case 'hasReminders':
                            return task.reminders && task.reminders.length > 0;
                        case 'isRecurring':
                            return !!task.recurrence_rule;
                        case 'hasDueDate':
                            return !!task.due_date;
                        case 'isOverdue':
                            return task.due_date && new Date(task.due_date) < new Date().setHours(0, 0, 0, 0) && !task.is_completed;
                        default:
                            return true;
                    }
                });
            });
        }

        return result;
    }, [tasks, filterTags, filterProjectIds, filterCategoryIds, filterPriorities, filterAttributes]);

    // Group tasks logic
    const tasksByGroup = useMemo(() => {
        const groups = { 'uncategorized': [] };
        (taskGroups || []).forEach(g => groups[g.id] = []);

        const taskMap = {};
        // Use filteredTasks instead of tasks
        (filteredTasks || []).forEach(t => taskMap[t.id] = { ...t, children: [] });

        (filteredTasks || []).forEach(t => {
            const taskWithChildren = taskMap[t.id];
            // Only process top-level tasks or ensure parent/child relationship holds
            if (t.parent_task_id && taskMap[t.parent_task_id]) {
                taskMap[t.parent_task_id].children.push(taskWithChildren);
            } else {
                const groupId = t.section_id || 'uncategorized';
                if (groups[groupId]) {
                    groups[groupId].push(taskWithChildren);
                } else {
                    groups['uncategorized'].push(taskWithChildren);
                }
            }
        });

        return groups;
    }, [filteredTasks, taskGroups]);

    return { filteredTasks, tasksByGroup };
}
